import {
  MedicalSystemRtSystemDto,
  CreateMedicalSystemRtSystemDto,
  BeamQualityDto,
  CreateBeamQualityDto,
  NumericListDto,
  CreateNumericListDto,
  CreateNumericListDtoTypeEnum,
  //
  NumericValueDto,
  CreateNumericValueDto,
  CreateNumericValueDtoTypeEnum,
  //
  EnumerationOptionDto,
  ReferencedEntityNullableLongDto,
  ReferencedEntityNullableLongDtoTypeEnum,
  //
  EnumerationValueDto,
  CreateEnumerationValueDto,
  CreateEnumerationValueDtoTypeEnum,
  //
  CreateElectronBeamQualityDto,
  CreateElectronBeamQualityDtoTypeEnum,
  CreatePhotonBeamQualityDtoTypeEnum,
  //
  LinearAcceleratorDto,
  LinearAcceleratorDtoTypeEnum,
  CreateLinearAcceleratorDto,
  CreateLinearAcceleratorDtoTypeEnum,
  CreatePhotonBeamQualityDto,
  ImagingMachineDto,
  ImagingMachineDtoTypeEnum,
  CreateImagingMachineDto,
  CreateImagingMachineDtoTypeEnum,
  OtherMedicalSystemDto,
  OtherMedicalSystemDtoTypeEnum,
  CreateOtherMedicalSystemDto,
  CreateOtherMedicalSystemDtoTypeEnum,
  SoftwareDto,
  SoftwareDtoTypeEnum,
  CreateSoftwareDto,
  CreateSoftwareDtoTypeEnum,
  OtherMvTherapyMachineDto,
  OtherMvTherapyMachineDtoTypeEnum,
  CreateOtherMvTherapyMachineDto,
  CreateOtherMvTherapyMachineDtoTypeEnum,
  OtherTreatmentMachineDto,
  OtherTreatmentMachineDtoTypeEnum,
  CreateOtherTreatmentMachineDto,
  CreateOtherTreatmentMachineDtoTypeEnum,
  PhotonBeamQualityDtoTypeEnum,
  ElectronBeamQualityDtoTypeEnum,
  CreatePhotonBeamQualitiesDtoTypeEnum,
  CreateElectronBeamQualitiesDtoTypeEnum,
} from '@/api/generator';

// BEAM QUALITY DTO
/**
 * toCreateNumericListDto
 */
function toCreateNumericListDto(numericListDto: NumericListDto): CreateNumericListDto {
  return {
    ...numericListDto,
    $type:
      CreateNumericListDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoNumericDtoCreateNumericListDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toCreateMandatoryNumericValueDto
 */
function toCreateMandatoryNumericValueDto(
  mandatoryNumericValueDto: NumericValueDto,
): CreateNumericValueDto {
  return {
    ...mandatoryNumericValueDto,
    $type:
      CreateNumericValueDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoNumericDtoCreateNumericValueDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toReferencedEntityNullableLongDto
 */
function toReferencedEntityNullableLongDto(
  enumerationOptionDto: EnumerationOptionDto,
): ReferencedEntityNullableLongDto {
  return {
    ...enumerationOptionDto,
    $type:
      ReferencedEntityNullableLongDtoTypeEnum.PtwPtw100AppZeroApplicationSharedCommonDtoReferencedEntityNullableLongDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toCreateEnumerationValueDto
 */
function toCreateEnumerationValueDto(
  optionalEnumerationValueDto: EnumerationValueDto,
): CreateEnumerationValueDto {
  return {
    ...optionalEnumerationValueDto,
    value:
      optionalEnumerationValueDto.value &&
      toReferencedEntityNullableLongDto(optionalEnumerationValueDto.value),
    $type:
      CreateEnumerationValueDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoEnumerationDtoCreateEnumerationValueDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toCreateBeamQualityDto
 */
export function toCreateBeamQualityDto(dto: BeamQualityDto): CreateBeamQualityDto {
  switch (dto.$type) {
    case PhotonBeamQualityDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoTreatmentMachinesConfigurationPhotonBeamQualityDtoPtwPtw100AppZeroApplicationShared:
      return {
        ...dto,
        availableDoseRates:
          dto.availableDoseRates && toCreateNumericListDto(dto.availableDoseRates),
        energy: toCreateMandatoryNumericValueDto(dto.energy),
        radiationType: toCreateEnumerationValueDto(dto.radiationType),
        highDoseTechniqueType:
          dto.highDoseTechniqueType && toCreateEnumerationValueDto(dto.highDoseTechniqueType),
        primaryFluenceShaping:
          dto.primaryFluenceShaping && toCreateEnumerationValueDto(dto.primaryFluenceShaping),
        $type:
          CreatePhotonBeamQualityDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoTreatmentMachinesConfigurationCreatePhotonBeamQualityDtoPtwPtw100AppZeroApplicationShared,
      };

    case ElectronBeamQualityDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoTreatmentMachinesConfigurationElectronBeamQualityDtoPtwPtw100AppZeroApplicationShared:
      return {
        ...dto,
        availableDoseRates:
          dto.availableDoseRates && toCreateNumericListDto(dto.availableDoseRates),
        energy: toCreateMandatoryNumericValueDto(dto.energy),
        radiationType: toCreateEnumerationValueDto(dto.radiationType),
        highDoseTechniqueType:
          dto.highDoseTechniqueType && toCreateEnumerationValueDto(dto.highDoseTechniqueType),

        $type:
          CreateElectronBeamQualityDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoTreatmentMachinesConfigurationCreateElectronBeamQualityDtoPtwPtw100AppZeroApplicationShared,
      };

    default:
      throw new Error('BeamQualityDto could not be found');
  }
}

// MEDICAL SYSTEM RT SYSTEM DTO MAPPINGS

/**
 * toCreateLinearAcceleratorDto
 */
function toCreateLinearAcceleratorDto(
  linearAcceleratorDto: LinearAcceleratorDto,
): CreateLinearAcceleratorDto {
  return {
    ...linearAcceleratorDto,
    beamQualitiesPhoton: {
      ...linearAcceleratorDto.beamQualitiesPhoton,
      values: linearAcceleratorDto.beamQualitiesPhoton?.values?.map((el) =>
        toCreateBeamQualityDto(el as BeamQualityDto),
      ) as Array<CreatePhotonBeamQualityDto>,
      $type:
        CreatePhotonBeamQualitiesDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoTreatmentMachinesConfigurationCreatePhotonBeamQualitiesDtoPtwPtw100AppZeroApplicationShared,
    },

    beamQualitiesElectron: {
      ...linearAcceleratorDto.beamQualitiesElectron,
      values: linearAcceleratorDto.beamQualitiesElectron?.values?.map((el) =>
        toCreateBeamQualityDto(el as BeamQualityDto),
      ) as Array<CreateElectronBeamQualityDto>,
      $type:
        CreateElectronBeamQualitiesDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoTreatmentMachinesConfigurationCreateElectronBeamQualitiesDtoPtwPtw100AppZeroApplicationShared,
    },

    $type:
      CreateLinearAcceleratorDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsCreateLinearAcceleratorDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toCreateImagingMachineDto
 */
function toCreateImagingMachineDto(imagingMachineDto: ImagingMachineDto): CreateImagingMachineDto {
  return {
    ...imagingMachineDto,

    $type:
      CreateImagingMachineDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsCreateImagingMachineDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toCreateOtherMedicalSystemDto
 */
function toCreateOtherMedicalSystemDto(
  imagingMachineDto: OtherMedicalSystemDto,
): CreateOtherMedicalSystemDto {
  return {
    ...imagingMachineDto,
    $type:
      CreateOtherMedicalSystemDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsCreateOtherMedicalSystemDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toCreateSoftwareDto
 */
function toCreateSoftwareDto(softwareDto: SoftwareDto): CreateSoftwareDto {
  return {
    ...softwareDto,
    $type:
      CreateSoftwareDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsCreateSoftwareDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toCreateOtherMvTreatmentMachineDto
 */
function toCreateOtherMvTreatmentMachineDto(
  softwareDto: OtherMvTherapyMachineDto,
): CreateOtherMvTherapyMachineDto {
  return {
    ...softwareDto,
    $type:
      CreateOtherMvTherapyMachineDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsCreateOtherMvTherapyMachineDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toCreateOtherTreatmentMachineDto
 */
function toCreateOtherTreatmentMachineDto(
  otherTreatmentMachineDto: OtherTreatmentMachineDto,
): CreateOtherTreatmentMachineDto {
  return {
    ...otherTreatmentMachineDto,
    $type:
      CreateOtherTreatmentMachineDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsCreateOtherTreatmentMachineDtoPtwPtw100AppZeroApplicationShared,
  };
}

/**
 * toCreateMedicalSystemRtSystemDto
 */
export function toCreateMedicalSystemRtSystemDto(
  dto: MedicalSystemRtSystemDto,
): CreateMedicalSystemRtSystemDto {
  switch (dto.$type) {
    case ImagingMachineDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsImagingMachineDtoPtwPtw100AppZeroApplicationShared:
      return toCreateImagingMachineDto(dto);

    case LinearAcceleratorDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsLinearAcceleratorDtoPtwPtw100AppZeroApplicationShared:
      return toCreateLinearAcceleratorDto(dto);

    case OtherMedicalSystemDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsOtherMedicalSystemDtoPtwPtw100AppZeroApplicationShared:
      return toCreateOtherMedicalSystemDto(dto);

    case SoftwareDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsSoftwareDtoPtwPtw100AppZeroApplicationShared:
      return toCreateSoftwareDto(dto);

    case OtherMvTherapyMachineDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsOtherMvTherapyMachineDtoPtwPtw100AppZeroApplicationShared:
      return toCreateOtherMvTreatmentMachineDto(dto);

    case OtherTreatmentMachineDtoTypeEnum.PtwPtw100AppZeroApplicationSharedMedicalSystemComponentDtoDescriptionsOtherTreatmentMachineDtoPtwPtw100AppZeroApplicationShared:
      return toCreateOtherTreatmentMachineDto(dto);

    default:
      throw new Error('MedicalSystemRtSystemDto could not be found');
  }
}
